import React, { useCallback, useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { z } from 'zod';

import InputSelectDropdown, {
  InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';
import { ETemplateStudioLayoutOptions } from 'components/TemplatesStudio/enums';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { Separator } from 'components/ui/separator';

import { templateStudioSchema } from '../../../validation-schema';
import { ThemesFormItem } from '../Components/ThemesFormItem';

const BUY_ONE_GET_MANY_DEFAULT_LAYOUT = 1;

const FREE_TILES_LAYOUT_OPTIONS: InputSelectDropdownOption[] = [
  { value: '1', label: '1 in a row' },
  { value: '2', label: '2 in a row' },
  { value: '3', label: '3 in a row' }
];

export const TemplateStudioBuyOneGetManyForm: React.FC = () => {
  const { control, watch, setValue } =
    useFormContext<z.infer<typeof templateStudioSchema>>();
  const { supportedThemesForRollingOffers } = useTemplateStudioState();

  const desktopLayoutOptions = useMemo(
    () =>
      Object.values(ETemplateStudioLayoutOptions).map((value) => ({
        value,
        label: value
      })),
    []
  );

  const currentValue = watch('buyOneGetMany');

  const handleEnsureBlockConfig = useCallback((): void => {
    if (currentValue) return;
    setValue(
      'buyOneGetMany',
      {
        desktopLayout: ETemplateStudioLayoutOptions.VERTICAL,
        themes: [],
        selectedDesignId: '',
        freeTilesLayout: BUY_ONE_GET_MANY_DEFAULT_LAYOUT
      },
      { shouldDirty: true, shouldValidate: true }
    );
  }, [currentValue, setValue]);

  useEffect(() => {
    handleEnsureBlockConfig();
  }, [handleEnsureBlockConfig]);

  if (!supportedThemesForRollingOffers || !currentValue) return null;

  return (
    <React.Fragment>
      <Separator className="bg-base-border w-[288px] ml-[-2px]" />

      <FormField
        control={control}
        name="buyOneGetMany.desktopLayout"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-black whitespace-nowrap">
                  Desktop layout
                </span>
                <div className="w-[120px]">
                  <InputSelectDropdown
                    options={desktopLayoutOptions}
                    defaultSelected={desktopLayoutOptions.find(
                      (layout) =>
                        layout.value ===
                        watch('buyOneGetMany.desktopLayout')
                    )}
                    onOptionChange={field.onChange}
                    placeholder="Select product display"
                    isHorizontal={true}
                    buttonClassName={'h-[30px]'}
                    iconClassName="text-xs text-base-mutedForeground"
                    data-testid="TemplatesStudio-BuyOneGetMany-desktopLayout-label"
                    buttonDataTestId="TemplatesStudio-BuyOneGetMany-desktopLayout-button"
                    listDataTestId="TemplatesStudio-BuyOneGetMany-desktopLayout-list"
                  />
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="buyOneGetMany.freeTilesLayout"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-black whitespace-nowrap">
                  Free tiles layout
                </span>
                <div className="w-[120px]">
                  <InputSelectDropdown
                    options={FREE_TILES_LAYOUT_OPTIONS}
                    defaultSelected={FREE_TILES_LAYOUT_OPTIONS.find(
                      (option) => Number(option.value) === field.value
                    )}
                    onOptionChange={(value) =>
                      field.onChange(Number(value))
                    }
                    placeholder="Select layout"
                    isHorizontal={true}
                    buttonClassName={'h-[30px]'}
                    iconClassName="text-xs text-base-mutedForeground"
                    data-testid="TemplatesStudio-BuyOneGetMany-freeTilesLayout-label"
                    buttonDataTestId="TemplatesStudio-BuyOneGetMany-freeTilesLayout-button"
                    listDataTestId="TemplatesStudio-BuyOneGetMany-freeTilesLayout-list"
                  />
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="buyOneGetMany.themes"
        render={({ field }) => (
          <ThemesFormItem
            onChange={field.onChange}
            value={field.value || []}
            supportedThemes={supportedThemesForRollingOffers}
            themesSelectDataTestId="TemplatesStudio-BuyOneGetMany-themes"
          />
        )}
      />
    </React.Fragment>
  );
};
